// index.tsx
import React, { useEffect, useState } from "react";
import { Table } from "antd";
import type { TableProps } from "antd";
import { DatatableProps } from "../../data/interface"; // Ensure correct path
import { SearchOutlined } from "@ant-design/icons";

const Datatable: React.FC<
  DatatableProps & {
    onSearchChange?: (text: string) => void;
    summary?: (currentData: readonly any[]) => React.ReactNode;
    scroll?: { x?: number | string; y?: number | string };
    pagination?:
      | false
      | {
          current: number;
          pageSize: number;
          total: number;
          onChange: (page: number, pageSize?: number) => void;
          pageSizeOptions?: (number | string)[];
          showSizeChanger?: boolean | any;
          className?: string;
        };
  }
> = ({
  columns,
  dataSource,
  Selection,
  onSelectionChange,
  rowKey = "id",
  selectedKeys = [],
  getCheckboxProps,
  onSearchChange,
  pagination,
  summary,
  scroll,
}) => {
  const [searchText, setSearchText] = useState<string>("");
  const [Selections, setSelections] = useState<any>(Selection);
  const [filteredDataSource, setFilteredDataSource] = useState(dataSource);

  const handleSelectChange = React.useCallback((newKeys: React.Key[]) => {
    onSelectionChange?.(newKeys); // Propagate changes to parent
  }, [onSelectionChange]);

  const handleSearch = () => {
    const filteredData = dataSource.filter((record) =>
      Object.values(record).some((field) =>
        String(field).toLowerCase().includes(searchText.toLowerCase())
      )
    );
    setFilteredDataSource(filteredData);
    onSearchChange?.(searchText); // Pass searchText to parent only when search button is clicked
  };

  const selectableKeys = React.useMemo(() => {
    return (filteredDataSource || dataSource)
      .filter((record) => {
        const props = getCheckboxProps ? getCheckboxProps(record) : {};
        return !props.disabled;
      })
      .map((record) => record[rowKey as keyof typeof record]);
  }, [filteredDataSource, dataSource, getCheckboxProps, rowKey]);

  const selectedKeysSet = new Set(selectedKeys);
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((key) => selectedKeysSet.has(key));

  const handleSelectAll = React.useCallback(() => {
    handleSelectChange(allSelected ? [] : selectableKeys);
  }, [handleSelectChange, allSelected, selectableKeys]);

  const rowSelection = React.useMemo(() => {
    return Selection
      ? {
          selectedRowKeys: selectedKeys,
          onChange: handleSelectChange,
          preserveSelectedRowKeys: true,
          columnTitle: (
            <a onClick={handleSelectAll} style={{ cursor: "pointer", whiteSpace: "nowrap", color: "#3D5EE1" }}>
              {allSelected ? "Deselect All" : "Select All"}
            </a>
          ),
          getCheckboxProps: (record: any) => ({
            id: record[rowKey as keyof typeof record],
            ...(getCheckboxProps ? getCheckboxProps(record) : {}),
          }),
        }
      : undefined;
  }, [Selection, selectedKeys, handleSelectChange, handleSelectAll, rowKey, getCheckboxProps, allSelected]);

  // Update Selections when Selection prop changes
  useEffect(() => {
    setSelections(Selection);
  }, [Selection]);

  // Update filteredDataSource when dataSource changes
  useEffect(() => {
    setFilteredDataSource(dataSource);
  }, [dataSource]);

  let showSizeChanger: boolean | any = true;
  if (pagination) {
    if (pagination.showSizeChanger !== undefined) {
      showSizeChanger = pagination.showSizeChanger;
    } else if (pagination.pageSizeOptions?.includes("All")) {
      showSizeChanger = {
        options: pagination.pageSizeOptions.map((opt: number | string) => ({
          label: opt === "All" ? "All" : String(opt),
          value: opt === "All" ? pagination.total || 0 : Number(opt),
        })),
      };
    }
  }

  const isSinglePage = pagination && pagination.total > 0 && pagination.pageSize >= pagination.total;
  const combinedClassName = [
    pagination && pagination.className,
    isSinglePage ? "hide-page-numbers" : ""
  ].filter(Boolean).join(" ");

  const tableProps: TableProps<any> = {
    className: "table datanew dataTable no-footer",
    columns,
    dataSource: filteredDataSource,
    rowKey: rowKey,
    pagination: pagination
      ? {
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: pagination.onChange,
          pageSizeOptions: pagination.pageSizeOptions || [10, 20, 30],
          showSizeChanger,
          showLessItems: true,
          className: combinedClassName || undefined,
          locale: {
            items_per_page: "",
          },
        }
      : false,
    ...(summary ? { summary } : {}),
    ...(scroll ? { scroll } : {}),
  };

  return (
    <>
      <style>
        {`
          .hide-page-numbers .ant-pagination-item,
          .hide-page-numbers .ant-pagination-prev,
          .hide-page-numbers .ant-pagination-next,
          .hide-page-numbers .ant-pagination-jump-prev,
          .hide-page-numbers .ant-pagination-jump-next {
            display: none !important;
          }
        `}
      </style>
      <div className="table-top-data d-flex px-3 justify-content-between align-items-center">
        <div className="page-range"></div>
        <div className="search-global text-right">
          <div className="input-group mb-3" style={{ width: 320 }}>
            <input
              type="search"
              className="form-control form-control-sm"
              value={searchText}
              placeholder="Search"
              onChange={(e) => {
                const newValue = e.target.value;
                setSearchText(newValue);
                if (newValue === "") {
                  setFilteredDataSource(dataSource);
                  onSearchChange?.("");
                }
              }}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              style={{ height: 32 }}
              aria-controls="DataTables_Table_0"
            />
            <button
              type="button"
              className="btn btn-sm btn-primary d-flex align-items-center justify-content-center"
              onClick={handleSearch}
              style={{
                cursor: "pointer",
                width: 40,
                height: 32,
                padding: 0,
              }}
            >
              <SearchOutlined style={{ fontSize: 16 }} />
            </button>
          </div>
        </div>
      </div>
      <Table {...tableProps} {...(Selections && { rowSelection })} />
    </>
  );
};

export default Datatable;
